import { App, type URLOpenListenerEvent } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { Network, type ConnectionStatus } from '@capacitor/network';
import {
  PushNotifications,
  type ActionPerformed,
  type PushNotificationSchema,
  type Token,
} from '@capacitor/push-notifications';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';

export const NATIVE_EVENTS = {
  appUrlOpen: 'zrs:native:app-url-open',
  pushAction: 'zrs:native:push-action',
  pushReceived: 'zrs:native:push-received',
  pushRegistration: 'zrs:native:push-registration',
  pushRegistrationError: 'zrs:native:push-registration-error',
  connectivity: 'zrs:native:connectivity',
  appState: 'zrs:native:app-state',
  cancelConfirmation: 'zrs:native:cancel-confirmation',
} as const;

let nativeAppActive = true;

export class OfflineMutationError extends Error {
  readonly code = 'NATIVE_OFFLINE_MUTATION_BLOCKED';

  constructor() {
    super('Offline mode is read-only. Reconnect before changing data.');
    this.name = 'OfflineMutationError';
  }
}

export class BackgroundMutationError extends Error {
  readonly code = 'NATIVE_BACKGROUND_MUTATION_BLOCKED';

  constructor() {
    super('Changes are blocked while the app is in the background.');
    this.name = 'BackgroundMutationError';
  }
}

export function isNativeApp(): boolean {
  return Capacitor.isNativePlatform();
}

function dispatch<T>(name: string, detail: T): void {
  window.dispatchEvent(new CustomEvent<T>(name, { detail }));
}

export function isTrustedAppUrl(candidate: string): boolean {
  try {
    const url = new URL(candidate);
    if (url.origin !== 'https://zrs.devpjt.com' || url.username || url.password) return false;
    if (url.pathname === '/auth/app/callback') return true;
    return /^\/(review|published)\/[a-zA-Z0-9-]{1,128}$/.test(url.pathname);
  } catch {
    return false;
  }
}

function dispatchDeepLink(event: URLOpenListenerEvent): void {
  if (!isTrustedAppUrl(event.url)) return;
  // Android delivers appUrlOpen before appStateChange(true) when a Custom Tab
  // returns through an App Link. Treat the trusted launch intent as foreground
  // so the one-time OIDC code exchange is not rejected by the mutation guard.
  nativeAppActive = true;
  dispatch(NATIVE_EVENTS.appUrlOpen, { url: new URL(event.url).toString() });
}

function dispatchPushAction(action: ActionPerformed): void {
  const data = action.notification.data ?? {};
  const possibleUrl = typeof data.url === 'string' ? data.url : undefined;
  const safeId = (value: unknown): string | undefined =>
    typeof value === 'string' && /^[a-zA-Z0-9-]{1,128}$/.test(value) ? value : undefined;
  const entityId = safeId(data.entityId);
  const notificationType = typeof data.notificationType === 'string' ? data.notificationType : '';
  const reviewId = safeId(data.reviewId) ?? (notificationType.startsWith('REVIEW') ? entityId : undefined);
  const publishedId = safeId(data.publicationId) ?? (notificationType.startsWith('REVIEW') ? undefined : entityId);
  const resolvedUrl = possibleUrl && isTrustedAppUrl(possibleUrl)
    ? possibleUrl
    : reviewId
      ? `https://zrs.devpjt.com/review/${reviewId}`
      : publishedId
        ? `https://zrs.devpjt.com/published/${publishedId}`
        : undefined;

  dispatch(NATIVE_EVENTS.pushAction, { ...action, resolvedUrl });
  if (resolvedUrl) dispatchDeepLink({ url: resolvedUrl });
}

function isMutation(input: RequestInfo | URL, init?: RequestInit): boolean {
  const method = (init?.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase();
  return ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);
}

/** Throws before a native mutation when Android reports no network connection. */
export async function assertMutationAllowed(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  if (!nativeAppActive) throw new BackgroundMutationError();
  const status = await Network.getStatus();
  if (!status.connected) throw new OfflineMutationError();
}

function installOfflineMutationGuard(): () => void {
  const originalFetch = window.fetch;
  const guardedFetch: typeof window.fetch = async (input, init) => {
    if (isMutation(input, init)) await assertMutationAllowed();
    return originalFetch.call(window, input, init);
  };
  window.fetch = guardedFetch;
  return () => {
    if (window.fetch === guardedFetch) window.fetch = originalFetch;
  };
}

/**
 * Installs the native event bridge once from the web application's bootstrap.
 * The web layer remains the source of truth for routing and API calls.
 */
export async function installNativeBridge(): Promise<() => Promise<void>> {
  if (!Capacitor.isNativePlatform()) return async () => undefined;

  const removeMutationGuard = installOfflineMutationGuard();
  const handles = await Promise.all([
    App.addListener('appUrlOpen', dispatchDeepLink),
    App.addListener('appStateChange', ({ isActive }) => {
      nativeAppActive = isActive;
      dispatch(NATIVE_EVENTS.appState, { isActive });
      if (!isActive) dispatch(NATIVE_EVENTS.cancelConfirmation, { reason: 'app-backgrounded' });
    }),
    Network.addListener('networkStatusChange', (status: ConnectionStatus) =>
      dispatch(NATIVE_EVENTS.connectivity, status),
    ),
    PushNotifications.addListener('registration', (token: Token) =>
      dispatch(NATIVE_EVENTS.pushRegistration, { token: token.value }),
    ),
    PushNotifications.addListener('registrationError', (error) =>
      dispatch(NATIVE_EVENTS.pushRegistrationError, { message: String(error.error) }),
    ),
    PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) =>
      dispatch(NATIVE_EVENTS.pushReceived, notification),
    ),
    PushNotifications.addListener('pushNotificationActionPerformed', dispatchPushAction),
  ]);

  const launch = await App.getLaunchUrl();
  if (launch?.url) dispatchDeepLink({ url: launch.url });
  dispatch(NATIVE_EVENTS.connectivity, await Network.getStatus());

  return async () => {
    removeMutationGuard();
    await Promise.all(handles.map((handle) => handle.remove()));
  };
}

/** Requests Android 13+ notification permission and registers for FCM. */
export async function registerForPushNotifications(): Promise<'registered' | 'denied' | 'web'> {
  if (!Capacitor.isNativePlatform()) return 'web';

  let permission = await PushNotifications.checkPermissions();
  if (permission.receive === 'prompt') permission = await PushNotifications.requestPermissions();
  if (permission.receive !== 'granted') return 'denied';

  await PushNotifications.register();
  return 'registered';
}

/** Clears delivered alerts and deletes the FCM token. The API registration must also be revoked. */
export async function unregisterFromPushNotifications(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await PushNotifications.removeAllDeliveredNotifications();
  } finally {
    await PushNotifications.unregister();
  }
}

/** Opens OIDC in a secure browser tab; the callback returns through an HTTPS App Link. */
export async function openOidcLogin(url: string): Promise<void> {
  const target = new URL(url);
  if (target.origin !== 'https://auth.devpjt.com' || target.username || target.password) {
    throw new Error('OIDC URL must use https://auth.devpjt.com');
  }
  await Browser.open({ url: target.toString(), presentationStyle: 'popover' });
}

export async function closeOidcBrowser(): Promise<void> {
  if (Capacitor.isNativePlatform()) await Browser.close();
}

function secureStorageKey(key: string): string {
  if (!/^[a-zA-Z0-9._-]{1,80}$/.test(key)) {
    throw new Error('Secure-storage key must be 1-80 safe ASCII characters');
  }
  return `zrs.${key}`;
}

function requireNativeSecureStorage(): void {
  if (!Capacitor.isNativePlatform()) {
    throw new Error('Secure storage is available only in the installed native app');
  }
}

/** Reads an Android Keystore-backed string, returning null only when the key is absent. */
export async function secureGet(key: string): Promise<string | null> {
  requireNativeSecureStorage();
  const namespacedKey = secureStorageKey(key);
  const { value: keys } = await SecureStoragePlugin.keys();
  if (!keys.includes(namespacedKey)) return null;
  const { value } = await SecureStoragePlugin.get({ key: namespacedKey });
  return value;
}

/** Writes a small authentication/device secret to Android Keystore-backed storage. */
export async function secureSet(key: string, value: string): Promise<void> {
  requireNativeSecureStorage();
  if (new TextEncoder().encode(value).byteLength > 32_768) {
    throw new Error('Secure-storage values are limited to 32 KiB');
  }
  const result = await SecureStoragePlugin.set({ key: secureStorageKey(key), value });
  if (!result.value) throw new Error('Secure storage write failed');
}

/** Removes a stored secret. Missing keys are treated as an idempotent success. */
export async function secureDelete(key: string): Promise<void> {
  requireNativeSecureStorage();
  const namespacedKey = secureStorageKey(key);
  const { value: keys } = await SecureStoragePlugin.keys();
  if (!keys.includes(namespacedKey)) return;
  const result = await SecureStoragePlugin.remove({ key: namespacedKey });
  if (!result.value) throw new Error('Secure storage delete failed');
}
